import { RefObject, useEffect } from "react";

export interface LineHoverHighlightOptions {
  selector: string;
}

const defaultOptions: LineHoverHighlightOptions = {
  selector: [
    ".ugc-description",
    ".ugc-profile-content p",
    ".ugc-pillar-item p",
    ".ugc-card-body h3",
    ".ugc-card-body p",
    ".ugc-work-item figcaption strong",
    ".ugc-work-item figcaption small",
    ".ugc-work-note",
    ".ugc-footer-left > p",
  ].join(", "),
};

/**
 * Adds line-level hover behavior by splitting target text into line spans.
 * Reusable across apps as long as matching CSS classes are present.
 */
export function useLineHoverHighlight(
  containerRef: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
  options: Partial<LineHoverHighlightOptions> = {}
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const mergedOptions = { ...defaultOptions, ...options };
    const measureCanvas = document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    if (!ctx) return;

    const makeLineSpans = (el: HTMLElement) => {
      const original = el.dataset.ugcOriginalText || el.textContent || "";
      if (!original.trim()) return;
      el.dataset.ugcOriginalText = original;

      const style = window.getComputedStyle(el);
      ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const maxWidth = el.clientWidth;
      if (!maxWidth) return;

      const words = original.trim().split(/\s+/);
      const lines: string[] = [];
      let currentLine = "";

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = ctx.measureText(testLine).width;
        if (testWidth > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) lines.push(currentLine);

      el.innerHTML = lines
        .map((line) => `<span class="ugc-line-text">${line}</span>`)
        .join("");
      el.classList.add("ugc-line-hover-target");
    };

    const bindLineHover = (el: HTMLElement) => {
      const onMove = (e: MouseEvent) => {
        const lines = Array.from(
          el.querySelectorAll(".ugc-line-text")
        ) as HTMLElement[];
        if (!lines.length) return;

        const rect = el.getBoundingClientRect();
        const relativeY = e.clientY - rect.top;
        const lineHeight = rect.height / lines.length;
        const activeIndex = Math.max(
          0,
          Math.min(lines.length - 1, Math.floor(relativeY / Math.max(1, lineHeight)))
        );
        lines.forEach((line, index) => {
          line.classList.toggle("is-active", index === activeIndex);
        });
      };

      const onLeave = () => {
        const lines = el.querySelectorAll(".ugc-line-text");
        lines.forEach((line) => line.classList.remove("is-active"));
      };

      el.addEventListener("mousemove", onMove);
      el.addEventListener("mouseleave", onLeave);
      return () => {
        el.removeEventListener("mousemove", onMove);
        el.removeEventListener("mouseleave", onLeave);
      };
    };

    const targets = Array.from(
      container.querySelectorAll(mergedOptions.selector)
    ) as HTMLElement[];
    const cleanupFns: Array<() => void> = [];

    targets.forEach((el) => {
      makeLineSpans(el);
      cleanupFns.push(bindLineHover(el));
    });

    return () => {
      cleanupFns.forEach((fn) => fn());
      targets.forEach((el) => {
        if (el.dataset.ugcOriginalText) {
          el.textContent = el.dataset.ugcOriginalText;
          el.classList.remove("ugc-line-hover-target");
        }
      });
    };
  }, [containerRef, options.selector, ...deps]);
}
